import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Layers, PlusCircle, ChevronRight, X, CheckCircle2, AlertCircle, RefreshCcw } from 'lucide-react';
import { useRole } from '../../hooks/useRole';
import { getCurrentOperatorName } from '../../utils/currentOperator';
import ManualAuthorityEditor, {
  emptyManualAuthority,
  manualAuthorityPayload,
  validateManualAuthority,
  type ManualAuthorityDraft,
} from '../../features/abf/onboarding/ManualAuthorityEditor';

/**
 * Reference-data workspace for securitized / structured instruments.
 *
 * Defines the instrument ONCE so the thin trade tickets can reference it by CUSIP
 * — the "define the instrument once" half of the reference-data / transaction-data
 * separation. Two layouts:
 *   - deal→tranche (RMBS, CMBS, CLO): a deal/pool master + a tranche detail list.
 *   - flat (ABF, MSR): a single instrument list with a rich create/edit form.
 *
 * Field schemas + endpoints are config-driven per asset class (ASSET_CLASSES).
 */

// ---- field schema ----
type FieldType = 'text' | 'number' | 'date' | 'select';
interface FieldDef { key: string; label: string; type?: FieldType; options?: string[]; required?: boolean; span?: number; }

const AGENCIES = ['NON_AGENCY', 'FNMA', 'FHLMC', 'GNMA'];
const RMBS_DEAL_TYPES = ['PRIME_JUMBO', 'AGENCY_PASSTHROUGH', 'NON_QM', 'RPL', 'NPL', 'CRT', 'SECOND_LIEN', 'HELOC'];
const PRODUCTS = ['FRM_30', 'FRM_15', 'ARM', 'HYBRID_ARM', 'IO'];
const RMBS_TRANCHE_CLASSES = ['SENIOR', 'MEZZ', 'SUBORDINATE', 'NR', 'IO', 'PO', 'RESIDUAL'];
const STRUCTURES = ['SEQUENTIAL', 'PAC', 'TAC', 'SUPPORT', 'NAS', 'SHIFTING_INTEREST', 'NIM'];
const COUPON_TYPES = ['FIXED', 'FLOATING', 'WAC', 'WAC_CAP', 'IO_NOTIONAL'];
const CMBS_DEAL_TYPES = ['CONDUIT', 'SASB', 'AGENCY_CMBS'];
const CMBS_TRANCHE_CLASSES = ['SUPER_SENIOR', 'SENIOR_AAA', 'AS', 'AA', 'A', 'BBB', 'BB', 'B', 'NR', 'IO_X'];
const ABS_SECTORS = ['AUTO_LOAN', 'AUTO_LEASE', 'CREDIT_CARD', 'STUDENT_LOAN', 'EQUIPMENT', 'AIRCRAFT', 'CONSUMER'];
const CLO_DEAL_TYPES = ['BSL', 'MM', 'STATIC', 'MANAGED'];
const CLO_TRANCHE_TYPES = ['EQUITY', 'SENIOR', 'MEZZANINE', 'JUNIOR'];
const CLO_COUPON_TYPES = ['', 'FLOATING', 'FIXED', 'RESIDUAL'];
const ABF_STRUCTURES = ['REVOLVER', 'TERM', 'HYBRID'];
// Canonical collateral classes — must match abf_collateral_class_profile.collateral_class
// so the facility picks up its class profile (extended tape fields + eligibility template).
const ABF_COLLATERAL_CLASSES = ['CONSUMER_LOANS', 'AUTO_LOANS', 'EQUIPMENT_FINANCE', 'FUND_FINANCE', 'AVIATION', 'ROYALTIES', 'LITIGATION_FINANCE', 'SOLAR_PPA'];

/** Naive "+ s" rendered "ABF Facilitys" on the reference-data header. */
const pluralize = (noun: string) =>
  /[^aeiou]y$/i.test(noun) ? `${noun.slice(0, -1)}ies` : `${noun}s`;
const FINANCING_INDICES = ['SOFR', 'TERM_SOFR', 'PRIME', 'LIBOR', 'FIXED'];
const MSR_AGENCIES = ['FNMA', 'FHLMC', 'GNMA', 'PRIVATE'];

interface AssetClassDef {
  key: string;
  label: string;
  enabled: boolean;
  mode: 'pool-tranche' | 'deal-with-tranche' | 'flat';
  dealNoun: string;            // "Deal" | "Facility" | "Package"
  trancheNoun?: string;        // "Tranche" (omitted for flat)
  listUrl: string;             // GET deals/facilities
  tranchesUrl?: (id: number) => string;
  dealCreateUrl?: string;      // POST create pool/deal/flat-instrument
  trancheCreateUrl?: (id: number) => string;
  dealFields: FieldDef[];
  trancheFields?: FieldDef[];
  idKey: string;               // response/list key for the master id ("poolId")
}

const ASSET_CLASSES: AssetClassDef[] = [
  {
    key: 'RMBS', label: 'RMBS', enabled: true, mode: 'pool-tranche',
    dealNoun: 'Deal', trancheNoun: 'Tranche', idKey: 'poolId',
    listUrl: '/api/credit/rmbs/pools',
    tranchesUrl: (id) => `/api/credit/rmbs/pools/${id}/tranches`,
    dealCreateUrl: '/api/credit/rmbs/reference/pool',
    trancheCreateUrl: (id) => `/api/credit/rmbs/reference/pools/${id}/tranche`,
    dealFields: [
      { key: 'dealName', label: 'Deal Name', required: true, span: 2 },
      { key: 'agency', label: 'Agency', type: 'select', options: AGENCIES },
      { key: 'dealType', label: 'Deal Type', type: 'select', options: RMBS_DEAL_TYPES },
      { key: 'issuerName', label: 'Issuer' },
      { key: 'servicerName', label: 'Servicer' },
      { key: 'productType', label: 'Product', type: 'select', options: PRODUCTS },
      { key: 'maturityDate', label: 'Final Maturity', type: 'date' },
      { key: 'originalPoolBalance', label: 'Pool Balance', type: 'number' },
      { key: 'numLoansAtIssuance', label: '# Loans', type: 'number' },
      { key: 'waFicoAtIssuance', label: 'WA FICO', type: 'number' },
      { key: 'waLtvPctAtIssuance', label: 'WA LTV (%)', type: 'number' },
      { key: 'wacPctAtIssuance', label: 'WAC (%)', type: 'number' },
      { key: 'cpr3mPct', label: '3M CPR (%)', type: 'number' },
      { key: 'cdrPct', label: 'CDR (%)', type: 'number' },
      { key: 'delinquency90PlusPct', label: '90+ DQ (%)', type: 'number' },
    ],
    trancheFields: [
      { key: 'trancheName', label: 'Tranche', required: true },
      { key: 'trancheClass', label: 'Class', type: 'select', options: RMBS_TRANCHE_CLASSES },
      { key: 'structureType', label: 'Structure', type: 'select', options: STRUCTURES },
      { key: 'paymentPriority', label: 'Priority', type: 'number' },
      { key: 'originalBalance', label: 'Tranche Size', type: 'number', required: true },
      { key: 'couponType', label: 'Coupon Type', type: 'select', options: COUPON_TYPES },
      { key: 'couponRate', label: 'Coupon (%)', type: 'number' },
      { key: 'spreadBps', label: 'Spread (bps)', type: 'number' },
      { key: 'originalCreditSupportPct', label: 'Credit Support (%)', type: 'number' },
      { key: 'weightedAvgLifeYears', label: 'WAL (yrs)', type: 'number' },
      { key: 'attachmentPoint', label: 'Attachment (%)', type: 'number' },
      { key: 'detachmentPoint', label: 'Detachment (%)', type: 'number' },
      { key: 'moodysRating', label: "Moody's" },
      { key: 'spRating', label: 'S&P' },
      { key: 'fitchRating', label: 'Fitch' },
      { key: 'maturityDate', label: 'Final Maturity', type: 'date' },
    ],
  },
  {
    key: 'CMBS', label: 'CMBS', enabled: true, mode: 'pool-tranche',
    dealNoun: 'Deal', trancheNoun: 'Tranche', idKey: 'poolId',
    listUrl: '/api/credit/cmbs-acq/pools',
    tranchesUrl: (id) => `/api/credit/cmbs-acq/pools/${id}/tranches`,
    dealCreateUrl: '/api/credit/cmbs-acq/reference/pool',
    trancheCreateUrl: (id) => `/api/credit/cmbs-acq/reference/pools/${id}/tranche`,
    dealFields: [
      { key: 'dealName', label: 'Deal Name', required: true, span: 2 },
      { key: 'dealType', label: 'Deal Type', type: 'select', options: CMBS_DEAL_TYPES },
      { key: 'masterServicer', label: 'Master Servicer' },
      { key: 'specialServicer', label: 'Special Servicer' },
      { key: 'trusteeName', label: 'Trustee' },
      { key: 'originalPoolBalance', label: 'Pool Balance', type: 'number' },
      { key: 'maturityDate', label: 'Legal Final Maturity', type: 'date' },
    ],
    trancheFields: [
      { key: 'trancheName', label: 'Tranche', required: true },
      { key: 'trancheClass', label: 'Class', type: 'select', options: CMBS_TRANCHE_CLASSES },
      { key: 'paymentPriority', label: 'Priority', type: 'number' },
      { key: 'originalBalance', label: 'Tranche Size', type: 'number', required: true },
      { key: 'couponType', label: 'Coupon Type', type: 'select', options: COUPON_TYPES },
      { key: 'couponRate', label: 'Coupon (%)', type: 'number' },
      { key: 'spreadBps', label: 'Spread (bps)', type: 'number' },
      { key: 'originalCreditSupportPct', label: 'Credit Support (%)', type: 'number' },
      { key: 'weightedAvgLifeYears', label: 'WAL (yrs)', type: 'number' },
      { key: 'attachmentPoint', label: 'Attachment (%)', type: 'number' },
      { key: 'detachmentPoint', label: 'Detachment (%)', type: 'number' },
      { key: 'moodysRating', label: "Moody's" },
      { key: 'spRating', label: 'S&P' },
      { key: 'fitchRating', label: 'Fitch' },
      { key: 'kbraRating', label: 'KBRA' },
      { key: 'maturityDate', label: 'Legal Final Maturity', type: 'date' },
    ],
  },
  {
    // CLO: the deal + its equity tranche are created together (one combined form
    // posting CLOEquityRequest). Listed as deals; the tranche shows in detail.
    key: 'CLO', label: 'CLO', enabled: true, mode: 'deal-with-tranche',
    dealNoun: 'Deal', trancheNoun: 'Tranche', idKey: 'poolId',
    listUrl: '/api/credit/clo-equity/deals',
    tranchesUrl: (id) => `/api/credit/clo-equity/deals/${id}/tranches`,
    dealCreateUrl: '/api/credit/clo-equity/reference/tranche',
    dealFields: [
      { key: 'dealName', label: 'Deal Name', required: true, span: 2 },
      { key: 'dealTicker', label: 'Deal Ticker' },
      { key: 'dealType', label: 'Deal Type', type: 'select', options: CLO_DEAL_TYPES },
      { key: 'managerName', label: 'Manager' },
      { key: 'trusteeName', label: 'Trustee' },
      { key: 'originalDealSize', label: 'Deal Size', type: 'number' },
      { key: 'closingDate', label: 'Closing Date', type: 'date' },
      { key: 'reinvestmentEndDate', label: 'Reinvestment End', type: 'date' },
      { key: 'maturityDate', label: 'Maturity', type: 'date' },
      { key: 'warf', label: 'WARF', type: 'number' },
      { key: 'was', label: 'WAS (%)', type: 'number' },
      { key: 'wal', label: 'WAL (yrs)', type: 'number' },
      { key: 'diversityScore', label: 'Diversity', type: 'number' },
      // tranche fields (carried on the same CLOEquityRequest). Tranche Type drives
      // the SMF security type + asset class (debt vs equity) server-side.
      { key: 'trancheType', label: 'Tranche Type', type: 'select', options: CLO_TRANCHE_TYPES },
      { key: 'trancheName', label: 'Tranche (e.g. A-1, B, SUB)' },
      { key: 'originalBalance', label: 'Tranche Balance', type: 'number' },
      { key: 'couponType', label: 'Coupon Type', type: 'select', options: CLO_COUPON_TYPES },
      { key: 'couponRate', label: 'Coupon (%)', type: 'number' },
      { key: 'spreadBps', label: 'Spread (bps)', type: 'number' },
      { key: 'moodysRating', label: "Moody's" },
      { key: 'spRating', label: 'S&P' },
      { key: 'fitchRating', label: 'Fitch' },
    ],
  },
  {
    // ABS deal and initial tranche are governed by one prepare request. The
    // backend mints the SMF instrument but never a position; acquisition still
    // starts from the thin ABS trade ticket.
    key: 'ABS', label: 'ABS', enabled: true, mode: 'deal-with-tranche',
    dealNoun: 'Deal / Tranche', trancheNoun: 'Tranche', idKey: 'dealId',
    listUrl: '/api/credit/abs/deals',
    tranchesUrl: (id) => `/api/credit/abs/deals/${id}/tranches`,
    dealCreateUrl: '/api/credit/abs/prepare',
    dealFields: [
      { key: 'dealName', label: 'Deal Name', required: true, span: 2 },
      { key: 'dealTicker', label: 'Deal Ticker' },
      { key: 'sector', label: 'Collateral Sector', type: 'select', options: ABS_SECTORS, required: true },
      { key: 'issuerName', label: 'Issuer' },
      { key: 'sponsorName', label: 'Sponsor' },
      { key: 'servicerName', label: 'Servicer' },
      { key: 'trusteeName', label: 'Trustee' },
      { key: 'currency', label: 'Currency', type: 'select', options: ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'] },
      { key: 'closingDate', label: 'Closing Date', type: 'date' },
      { key: 'cutoffDate', label: 'Cutoff Date', type: 'date' },
      { key: 'firstPaymentDate', label: 'First Payment', type: 'date' },
      { key: 'revolvingEndDate', label: 'Revolving End', type: 'date' },
      { key: 'legalFinalMaturity', label: 'Legal Final', type: 'date' },
      { key: 'originalPoolBalance', label: 'Original Pool Balance', type: 'number', required: true },
      { key: 'trancheName', label: 'Initial Tranche', required: true },
      { key: 'trancheClass', label: 'Tranche Class' },
      { key: 'paymentPriority', label: 'Payment Priority', type: 'number', required: true },
      { key: 'originalFace', label: 'Original Tranche Face', type: 'number', required: true },
      { key: 'couponType', label: 'Coupon Type', type: 'select', options: ['FIXED', 'FLOATING'] },
      { key: 'couponRate', label: 'Coupon (%)', type: 'number' },
      { key: 'rateIndex', label: 'Rate Index', type: 'select', options: ['SOFR', 'TERM_SOFR', 'PRIME', 'EURIBOR', 'SONIA'] },
      { key: 'spreadBps', label: 'Spread (bps)', type: 'number' },
      { key: 'paymentFrequency', label: 'Payment Frequency', type: 'select', options: ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL'] },
      { key: 'rating', label: 'Rating' },
      { key: 'originalCreditSupportPct', label: 'Credit Support (%)', type: 'number' },
      { key: 'expectedWalYears', label: 'Expected WAL (yrs)', type: 'number' },
      { key: 'expectedMaturity', label: 'Expected Maturity', type: 'date' },
      { key: 'intexDealId', label: 'Intex Deal ID' },
      { key: 'intexTrancheId', label: 'Intex Tranche ID' },
      { key: 'annualPrepayPct', label: 'Annual Prepay % (Auto/Student/Equipment/Consumer)', type: 'number' },
      { key: 'annualDefaultPct', label: 'Annual Default %', type: 'number' },
      { key: 'severityPct', label: 'Severity % (Auto Loan/Consumer)', type: 'number' },
      { key: 'returnRatePct', label: 'Lease Return % (Auto Lease)', type: 'number' },
      { key: 'residualValueHaircutPct', label: 'Residual Haircut % (Lease/Aircraft)', type: 'number' },
      { key: 'paymentRatePct', label: 'Monthly Payment Rate % (Credit Card)', type: 'number' },
      { key: 'portfolioYieldPct', label: 'Portfolio Yield % (Credit Card)', type: 'number' },
      { key: 'chargeOffRatePct', label: 'Charge-off Rate % (Credit Card)', type: 'number' },
      { key: 'defermentPct', label: 'Deferment % (Student)', type: 'number' },
      { key: 'residualValuePct', label: 'Residual Value % (Equipment)', type: 'number' },
      { key: 'leaseRatePct', label: 'Lease Rate % (Aircraft)', type: 'number' },
      { key: 'maintenanceReservePct', label: 'Maintenance Reserve % (Aircraft)', type: 'number' },
    ],
  },
  {
    key: 'ABF', label: 'ABF', enabled: true, mode: 'flat',
    dealNoun: 'Facility', idKey: 'facilityId',
    listUrl: '/api/credit/abf/facilities',
    dealCreateUrl: '/api/credit/abf/reference',
    dealFields: [
      { key: 'facilityName', label: 'Facility Name', required: true, span: 2 },
      { key: 'facilityCode', label: 'Facility Code (lender reference)' },
      { key: 'externalId', label: 'External Reference (system of record)' },
      { key: 'obligorName', label: 'Obligor / Borrower', required: true },
      { key: 'collateralType', label: 'Collateral Type', type: 'select', options: ABF_COLLATERAL_CLASSES, required: true },
      { key: 'abfStructure', label: 'Structure', type: 'select', options: ABF_STRUCTURES, required: true },
      { key: 'currency', label: 'Currency', type: 'select', options: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'], required: true },
      { key: 'sponsorName', label: 'Sponsor' },
      { key: 'originatorName', label: 'Originator' },
      { key: 'servicerName', label: 'Servicer' },
      { key: 'adminAgentName', label: 'Admin Agent' },
      { key: 'totalCommitment', label: 'Total Commitment', type: 'number', required: true },
      { key: 'drawnAmount', label: 'Drawn Amount', type: 'number' },
      { key: 'advanceRatePct', label: 'Contract Advance Rate (%)', type: 'number', required: true },
      { key: 'reserveAmount', label: 'Contract Reserve', type: 'number', required: true },
      { key: 'financingIndex', label: 'Financing Index', type: 'select', options: FINANCING_INDICES, required: true },
      { key: 'spreadBps', label: 'Spread (bps)', type: 'number', required: true },
      { key: 'unusedFeeBps', label: 'Unused Fee (bps)', type: 'number' },
      { key: 'closingDate', label: 'Closing Date', type: 'date' },
      { key: 'effectiveDate', label: 'Contract Effective Date', type: 'date', required: true },
      { key: 'maturityDate', label: 'Maturity', type: 'date', required: true },
    ],
  },
  {
    // SMF/economics split for listed options: define the contract ONCE here;
    // the options ticket references it by contractId and captures only trade
    // economics. POST /define mints dim_option_contract + an enriched
    // dim_security_master row (Derivatives/Options taxonomy + canonical
    // classification), no trade-side fields.
    key: 'OPTIONS', label: 'Listed Options', enabled: true, mode: 'flat',
    dealNoun: 'Contract', idKey: 'contractId',
    listUrl: '/api/derivatives/options/contracts?q=',
    dealCreateUrl: '/api/derivatives/options/define',
    dealFields: [
      { key: 'contractSymbol', label: 'Contract Symbol (OCC / label)', required: true, span: 2 },
      { key: 'contractName', label: 'Contract Name' },
      { key: 'underlyingSymbol', label: 'Underlying Symbol', required: true },
      { key: 'assetClass', label: 'Underlying Class', type: 'select', options: ['EQUITY', 'INDEX', 'FX', 'COMMODITY', 'RATES'] },
      { key: 'optionType', label: 'Type', type: 'select', options: ['CALL', 'PUT'], required: true },
      { key: 'exerciseStyle', label: 'Exercise Style', type: 'select', options: ['AMERICAN', 'EUROPEAN'] },
      { key: 'settlementType', label: 'Settlement', type: 'select', options: ['PHYSICAL', 'CASH'] },
      { key: 'exchange', label: 'Exchange' },
      { key: 'strikePrice', label: 'Strike', type: 'number', required: true },
      { key: 'contractMultiplier', label: 'Multiplier (shares/contract)', type: 'number', required: true },
      { key: 'expiryDate', label: 'Expiry', type: 'date', required: true },
      { key: 'currency', label: 'Currency', type: 'select', options: ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'] },
      { key: 'underlyingPrice', label: 'Underlying Spot (inception mark)', type: 'number' },
      { key: 'impliedVol', label: 'Implied Vol (decimal, 0.25)', type: 'number' },
      { key: 'riskFreeRate', label: 'Risk-Free Rate (decimal, 0.045)', type: 'number' },
    ],
  },
  {
    // SMF/economics split for listed futures: define the contract ONCE here;
    // the futures ticket references it by contractId and captures only trade
    // economics. POST /define mints dim_futures_contract + an enriched
    // dim_security_master row (Derivatives/Futures taxonomy + canonical
    // classification), no trade-side fields.
    key: 'FUTURES', label: 'Futures', enabled: true, mode: 'flat',
    dealNoun: 'Contract', idKey: 'contractId',
    listUrl: '/api/derivatives/futures/contracts',
    dealCreateUrl: '/api/derivatives/futures/define',
    dealFields: [
      { key: 'contractSymbol', label: 'Contract Symbol (e.g. ZNZ6)', required: true, span: 2 },
      { key: 'contractName', label: 'Contract Name' },
      { key: 'underlyingAsset', label: 'Underlying (e.g. 10Y UST, S&P 500)', required: true },
      { key: 'assetClass', label: 'Asset Class', type: 'select', required: true, options: ['RATES', 'EQUITY_INDEX', 'COMMODITY', 'FX', 'ENERGY', 'METAL', 'AGRICULTURE'] },
      { key: 'exchange', label: 'Exchange' },
      { key: 'clearingHouse', label: 'Clearing House' },
      { key: 'contractMultiplier', label: 'Multiplier ($/pt)', type: 'number', required: true },
      { key: 'tickSize', label: 'Tick Size', type: 'number', required: true },
      { key: 'expiryDate', label: 'Expiry', type: 'date', required: true },
      { key: 'lastTradingDate', label: 'Last Trading Date', type: 'date' },
      { key: 'deliveryMethod', label: 'Delivery', type: 'select', options: ['CASH', 'PHYSICAL'] },
      { key: 'currency', label: 'Currency', type: 'select', options: ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'] },
      { key: 'initialMarginPerContract', label: 'Initial Margin / Contract', type: 'number' },
      { key: 'maintenanceMarginPerContract', label: 'Maintenance Margin / Contract', type: 'number' },
      { key: 'ctdModifiedDuration', label: 'CTD Mod Duration (RATES only)', type: 'number' },
      { key: 'indexBeta', label: 'Index Beta (EQUITY_INDEX only)', type: 'number' },
    ],
  },
  {
    key: 'MSR', label: 'MSR', enabled: true, mode: 'flat',
    dealNoun: 'Package', idKey: 'msrId',
    listUrl: '/api/credit/msr',
    dealCreateUrl: '/api/credit/msr/reference',
    dealFields: [
      { key: 'msrName', label: 'MSR Package Name', required: true, span: 2 },
      { key: 'servicerName', label: 'Servicer' },
      { key: 'originator', label: 'Originator' },
      { key: 'agency', label: 'Agency', type: 'select', options: MSR_AGENCIES },
      { key: 'productType', label: 'Product', type: 'select', options: PRODUCTS },
      { key: 'upb', label: 'Serviced UPB', type: 'number', required: true },
      { key: 'numLoans', label: '# Loans', type: 'number' },
      { key: 'wacPct', label: 'WAC (%)', type: 'number' },
      { key: 'noteRatePct', label: 'Note Rate (%)', type: 'number' },
      { key: 'wamMonths', label: 'WAM (mo)', type: 'number' },
      { key: 'walaMonths', label: 'WALA (mo)', type: 'number' },
      { key: 'servicingFeeBps', label: 'Servicing Fee (bps)', type: 'number', required: true },
      { key: 'assumedCprPct', label: 'Assumed CPR (%)', type: 'number' },
      { key: 'discountRatePct', label: 'Discount Rate (%)', type: 'number' },
      { key: 'acquisitionMultiple', label: 'Acquisition Multiple', type: 'number' },
    ],
  },
];

// ---- styled ----
const Wrap = styled.div`color: #e2e8f0;`;
const Toolbar = styled.div`display: flex; align-items: center; gap: 8px; margin-bottom: 16px; flex-wrap: wrap;`;
const Chip = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  border: 1px solid ${p => (p.$active ? 'rgba(96,165,250,0.5)' : 'rgba(148,163,184,0.2)')};
  background: ${p => (p.$active ? 'rgba(59,130,246,0.18)' : 'transparent')};
  color: ${p => (p.$active ? '#60a5fa' : p.$disabled ? '#475569' : '#94a3b8')};
`;
const Cols = styled.div<{ $single?: boolean }>`display: grid; grid-template-columns: ${p => (p.$single ? '1fr' : '1fr 1fr')}; gap: 16px; @media (max-width: 1100px){ grid-template-columns: 1fr; }`;
const Card = styled.div`background: rgba(30,41,59,0.5); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; overflow: hidden;`;
const CardHead = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.06);`;
const CardTitle = styled.div`font-size: 13px; font-weight: 700; color: #f1f5f9;`;
const Btn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 10px; font-size: 12px; font-weight: 700;
  cursor: pointer; transition: all 0.15s ease; border: 1px solid transparent;
  ${p => p.$variant === 'primary'
    ? 'background: #2563eb; color: #fff; &:hover{ background:#1d4ed8; }'
    : 'background: transparent; color: #93c5fd; border-color: rgba(148,163,184,0.2); &:hover{ color:#fff; }'}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Row = styled.div<{ $active?: boolean }>`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 11px 16px; cursor: pointer;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  background: ${p => (p.$active ? 'rgba(59,130,246,0.12)' : 'transparent')};
  &:hover { background: rgba(255,255,255,0.03); }
`;
const RowMain = styled.div`min-width: 0;`;
const RowTitle = styled.div`font-size: 13px; font-weight: 600; color: #f1f5f9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const RowSub = styled.div`font-size: 11px; color: #94a3b8; margin-top: 2px;`;
const Mono = styled.span`font-family: 'JetBrains Mono', monospace; color: #60a5fa;`;
const Empty = styled.div`padding: 28px 16px; text-align: center; color: #64748b; font-size: 12px;`;
const FormGrid = styled.div`display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 16px;`;
const Field = styled.label<{ $span?: number }>`grid-column: span ${p => p.$span || 1}; display: block;`;
const FLabel = styled.div`font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin-bottom: 4px;`;
const inputCss = `width: 100%; box-sizing: border-box; background: #0f172a; border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 8px 10px; font-size: 13px; color: #fff; outline: none; &:focus{ border-color: rgba(96,165,250,0.5); }`;
const FInput = styled.input`${inputCss}`;
const FSelect = styled.select`${inputCss} appearance: none;`;
const FormActions = styled.div`display: flex; justify-content: flex-end; gap: 8px; padding: 0 16px 16px;`;
const Toast = styled.div<{ $err?: boolean }>`
  display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 600; margin-bottom: 12px;
  background: ${p => (p.$err ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)')};
  color: ${p => (p.$err ? '#f87171' : '#34d399')};
  border: 1px solid ${p => (p.$err ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)')};
`;

const numOrNull = (v: string) => (v === '' || v == null ? null : Number(v));

// Build the API payload from a form, coercing number/date fields.
const buildPayload = (fields: FieldDef[], form: Record<string, any>): Record<string, any> => {
  const out: Record<string, any> = {};
  fields.forEach(f => {
    const v = form[f.key];
    if (f.type === 'number') out[f.key] = numOrNull(v);
    else out[f.key] = v === '' || v == null ? null : v;
  });
  return out;
};

const ABS_SECTOR_TERM_KEYS = [
  'annualPrepayPct', 'annualDefaultPct', 'severityPct', 'returnRatePct',
  'residualValueHaircutPct', 'paymentRatePct', 'portfolioYieldPct',
  'chargeOffRatePct', 'defermentPct', 'residualValuePct', 'leaseRatePct',
  'maintenanceReservePct',
];

const buildAssetPayload = (def: AssetClassDef, form: Record<string, any>) => {
  const payload = buildPayload(def.dealFields, form);
  if (def.key !== 'ABS') return payload;
  payload.sectorTerms = {};
  ABS_SECTOR_TERM_KEYS.forEach(key => {
    if (payload[key] != null) payload.sectorTerms[key] = payload[key];
    delete payload[key];
  });
  payload.createdBy = getCurrentOperatorName();
  return payload;
};

const absDealForm = (deal: any): Record<string, any> => deal ? {
  ...deal,
  ...(deal.sectorTerms || {}),
  trancheName: '',
  trancheClass: '',
  paymentPriority: '',
  originalFace: '',
  couponType: '',
  couponRate: '',
  rateIndex: '',
  spreadBps: '',
  paymentFrequency: '',
  rating: '',
  originalCreditSupportPct: '',
  expectedWalYears: '',
  expectedMaturity: '',
  intexDealId: '',
  intexTrancheId: '',
} : {};

const FieldInput: React.FC<{ field: FieldDef; value: any; onChange: (v: any) => void }> = ({ field, value, onChange }) => (
  <Field $span={field.span}>
    <FLabel>{field.label}{field.required ? ' *' : ''}</FLabel>
    {field.type === 'select' ? (
      // Placeholder option so an untouched select stores NOTHING rather than
      // silently posting null while displaying the first option (E2E finding
      // 2026-08-05: futures Asset Class rendered "RATES" but persisted NULL,
      // killing the DV01 path). Required selects now fail validation until
      // explicitly picked.
      <FSelect value={value ?? ''} onChange={e => onChange(e.target.value)}>
        <option value="">Select…</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </FSelect>
    ) : (
      <FInput
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        step={field.type === 'number' ? 'any' : undefined}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
      />
    )}
  </Field>
);

interface Props {
  initialAssetClass?: string;
  openNewOnMount?: boolean;
  /**
   * Restrict which instrument classes this mount shows — the same define-once
   * engine serves two Reference Data tabs: Structured Products (securitized
   * classes) and Derivatives (listed contract classes such as OPTIONS).
   */
  classKeys?: string[];
}

const StructuredProductsScreen: React.FC<Props> = ({ initialAssetClass, openNewOnMount, classKeys }) => {
  const { canEdit } = useRole();
  const visibleClasses = classKeys && classKeys.length > 0
    ? ASSET_CLASSES.filter(a => classKeys.includes(a.key))
    : ASSET_CLASSES.filter(a => a.key !== 'OPTIONS' && a.key !== 'FUTURES'); // default mount = securitized classes only
  const initialDef = visibleClasses.find(a => a.key === (initialAssetClass || '').toUpperCase() && a.enabled) || visibleClasses[0];
  const [activeClass, setActiveClass] = useState<AssetClassDef>(initialDef);

  const [deals, setDeals] = useState<any[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<any | null>(null);
  const [tranches, setTranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [showDealForm, setShowDealForm] = useState(false);
  const [showTrancheForm, setShowTrancheForm] = useState(false);
  const [dealForm, setDealForm] = useState<Record<string, any>>({});
  const [manualAuthority, setManualAuthority] = useState<ManualAuthorityDraft>(emptyManualAuthority);
  const [trancheForm, setTrancheForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState('');

  const isFlat = activeClass.mode === 'flat';
  const hasTrancheList = activeClass.mode !== 'flat';
  const canAddTranche = activeClass.mode === 'pool-tranche';

  const fetchDeals = useCallback(async (def: AssetClassDef) => {
    if (!def.enabled) { setDeals([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(def.listUrl);
      setDeals(Array.isArray(data) ? data : []);
    } catch { setDeals([]); }
    finally { setLoading(false); }
  }, []);

  const fetchTranches = useCallback(async (def: AssetClassDef, masterId: number) => {
    if (!def.tranchesUrl) { setTranches([]); return; }
    try {
      const { data } = await axios.get(def.tranchesUrl(masterId));
      setTranches(Array.isArray(data) ? data : []);
    } catch { setTranches([]); }
  }, []);

  useEffect(() => { fetchDeals(activeClass); }, [activeClass, fetchDeals]);

  useEffect(() => {
    if (openNewOnMount && activeClass.enabled && canEdit) {
      setDealForm({});
      setManualAuthority(emptyManualAuthority());
      setShowDealForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const masterId = (d: any) => d?.[activeClass.idKey] ?? d?.poolId ?? d?.dealId ?? d?.facilityId ?? d?.msrId;

  const selectDeal = (deal: any) => {
    setSelectedDeal(deal);
    setShowTrancheForm(false);
    if (hasTrancheList) fetchTranches(activeClass, masterId(deal));
  };

  const flash = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const errMsg = (e: any) => e?.response?.data?.error || e?.response?.data?.message || 'Save failed';

  const requiredMissing = (fields: FieldDef[], form: Record<string, any>) =>
    fields.find(f => f.required && (form[f.key] == null || String(form[f.key]).trim() === ''));

  const saveDeal = async () => {
    const miss = requiredMissing(activeClass.dealFields, dealForm);
    if (miss) { setError(`${miss.label} is required`); return; }
    if (activeClass.key === 'ABF') {
      const authorityError = validateManualAuthority(manualAuthority);
      if (authorityError) { setError(authorityError); return; }
    }
    if (!activeClass.dealCreateUrl) return;
    setSaving(true); setError(null);
    try {
      const payload = buildAssetPayload(activeClass, dealForm);
      if (activeClass.key === 'ABF') payload.authority = manualAuthorityPayload(manualAuthority);
      const { data } = await axios.post(activeClass.dealCreateUrl, payload);
      setShowDealForm(false); setDealForm({}); setManualAuthority(emptyManualAuthority());
      flash(activeClass.key === 'ABF'
        ? `Facility saved; contract version ${data?.facilityVersion?.version_no || ''} awaits independent activation`
        : `${activeClass.dealNoun} saved`);
      await fetchDeals(activeClass);
    } catch (e) { setError(errMsg(e)); }
    finally { setSaving(false); }
  };

  const saveTranche = async () => {
    if (!selectedDeal || !activeClass.trancheCreateUrl || !activeClass.trancheFields) return;
    const miss = requiredMissing(activeClass.trancheFields, trancheForm);
    if (miss) { setError(`${miss.label} is required`); return; }
    setSaving(true); setError(null);
    try {
      await axios.post(activeClass.trancheCreateUrl(masterId(selectedDeal)), buildPayload(activeClass.trancheFields, trancheForm));
      setShowTrancheForm(false); setTrancheForm({});
      flash(`${activeClass.trancheNoun} saved`);
      await fetchTranches(activeClass, masterId(selectedDeal));
      await fetchDeals(activeClass);
    } catch (e) { setError(errMsg(e)); }
    finally { setSaving(false); }
  };

  const switchClass = (def: AssetClassDef) => {
    if (!def.enabled || def.key === activeClass.key) return;
    setActiveClass(def);
    setSelectedDeal(null); setTranches([]);
    setShowDealForm(false); setShowTrancheForm(false);
    setDealForm({}); setTrancheForm({}); setManualAuthority(emptyManualAuthority()); setError(null);
  };

  return (
    <Wrap>
      <Toolbar>
        {visibleClasses.map(def => (
          <Chip key={def.key} $active={activeClass.key === def.key} $disabled={!def.enabled}
            onClick={() => switchClass(def)} title={def.enabled ? def.label : 'Coming soon'}>
            {def.label}
          </Chip>
        ))}
        <div style={{ flex: 1 }} />
        <Btn $variant="ghost" onClick={() => fetchDeals(activeClass)}>
          <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </Btn>
      </Toolbar>

      {success && <Toast><CheckCircle2 size={15} /> {success}</Toast>}
      {error && <Toast $err><AlertCircle size={15} /> {error}<span style={{ flex: 1 }} /><X size={15} style={{ cursor: 'pointer' }} onClick={() => setError(null)} /></Toast>}

      <Cols $single={isFlat}>
        {/* Master list (deals / facilities / packages) */}
        <Card>
          <CardHead>
            <CardTitle>
              <Layers size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {activeClass.label} {pluralize(activeClass.dealNoun)}
            </CardTitle>
            {canEdit && (
              <Btn $variant="primary" onClick={() => {
                setDealForm(activeClass.key === 'ABS' ? absDealForm(selectedDeal) : {});
                setManualAuthority(emptyManualAuthority());
                setShowDealForm(true);
              }}>
                <PlusCircle size={14} /> New {activeClass.dealNoun}
              </Btn>
            )}
          </CardHead>

          {showDealForm && (
            <>
              <FormGrid>
                {activeClass.dealFields.map(f => (
                  <FieldInput key={f.key} field={f} value={dealForm[f.key]}
                    onChange={v => {
                      setDealForm(p => ({ ...p, [f.key]: v }));
                      if (activeClass.key === 'ABF' && f.key === 'effectiveDate') {
                        setManualAuthority(current => ({ ...current, effectiveFrom: v }));
                      }
                    }} />
                ))}
                {activeClass.key === 'ABF' && <ManualAuthorityEditor
                  value={manualAuthority}
                  onChange={setManualAuthority}
                  knownTerms={dealForm}
                />}
              </FormGrid>
              <FormActions>
                  <Btn $variant="ghost" onClick={() => { setShowDealForm(false); setDealForm({}); setManualAuthority(emptyManualAuthority()); }}>Cancel</Btn>
                <Btn $variant="primary" onClick={saveDeal} disabled={saving}>{saving ? 'Saving…' : `Save ${activeClass.dealNoun}`}</Btn>
              </FormActions>
            </>
          )}

          {deals.length === 0 && !loading ? (
            <Empty>No {activeClass.label} {activeClass.dealNoun.toLowerCase()}s yet. Create one to get started.</Empty>
          ) : (
            deals.map(d => {
              const id = masterId(d);
              return (
                <Row key={id} $active={masterId(selectedDeal) === id}
                  onClick={() => (hasTrancheList ? selectDeal(d) : undefined)}
                  style={isFlat ? { cursor: 'default' } : undefined}>
                  <RowMain>
                    <RowTitle>{d.dealName || d.facilityName || d.msrName || d.contractSymbol}</RowTitle>
                    <RowSub>
                      {isFlat
                        ? activeClass.key === 'OPTIONS'
                          ? <>{[d.underlyingSymbol, d.optionType, d.strikePrice != null ? `K ${d.strikePrice}` : null,
                                d.expiryDate ? `exp ${String(d.expiryDate).slice(0, 10)}` : null,
                                d.contractMultiplier != null ? `×${d.contractMultiplier}` : null,
                                d.exchange].filter(Boolean).join(' · ')}</>
                          : activeClass.key === 'FUTURES'
                          ? <>{[d.underlyingAsset, d.assetClass,
                                d.expiryDate ? `exp ${String(d.expiryDate).slice(0, 10)}` : null,
                                d.contractMultiplier != null ? `×${d.contractMultiplier}` : null,
                                d.exchange].filter(Boolean).join(' · ')}</>
                          : <>{d.cusip ? <Mono>{d.cusip}</Mono> : null}{d.obligorName ? ` · ${d.obligorName}` : ''}{d.collateralType ? ` · ${d.collateralType}` : ''}{d.servicerName && activeClass.key === 'MSR' ? ` · ${d.servicerName}` : ''}</>
                        : <>{activeClass.key === 'ABS'
                          ? [d.sector, d.issuerName, d.currency].filter(Boolean).join(' · ')
                          : [d.agency, d.dealType, d.managerName].filter(Boolean).join(' · ')} · {d.trancheCount || 0} {activeClass.trancheNoun?.toLowerCase()}{(d.trancheCount || 0) === 1 ? '' : 's'}</>}
                    </RowSub>
                  </RowMain>
                  {hasTrancheList && <ChevronRight size={16} color="#64748b" />}
                </Row>
              );
            })
          )}
        </Card>

        {/* Tranche detail (deal→tranche classes only) */}
        {hasTrancheList && (
          <Card>
            <CardHead>
              <CardTitle>{selectedDeal ? `${activeClass.trancheNoun}s — ${selectedDeal.dealName}` : `${activeClass.trancheNoun}s`}</CardTitle>
              {canEdit && selectedDeal && canAddTranche && (
                <Btn $variant="primary" onClick={() => { setTrancheForm({}); setShowTrancheForm(true); }}>
                  <PlusCircle size={14} /> Add {activeClass.trancheNoun}
                </Btn>
              )}
            </CardHead>

            {showTrancheForm && selectedDeal && activeClass.trancheFields && (
              <>
                <FormGrid>
                  {activeClass.trancheFields.map(f => (
                    <FieldInput key={f.key} field={f} value={trancheForm[f.key]}
                      onChange={v => setTrancheForm(p => ({ ...p, [f.key]: v }))} />
                  ))}
                </FormGrid>
                <FormActions>
                  <Btn $variant="ghost" onClick={() => { setShowTrancheForm(false); setTrancheForm({}); }}>Cancel</Btn>
                  <Btn $variant="primary" onClick={saveTranche} disabled={saving}>{saving ? 'Saving…' : `Save ${activeClass.trancheNoun}`}</Btn>
                </FormActions>
              </>
            )}

            {!selectedDeal ? (
              <Empty>Select a {activeClass.dealNoun.toLowerCase()} to view its {activeClass.trancheNoun?.toLowerCase()}s.</Empty>
            ) : tranches.length === 0 ? (
              <Empty>No {activeClass.trancheNoun?.toLowerCase()}s yet. {canAddTranche ? 'Add one — it mints a CUSIP the trade ticket can reference.' : ''}</Empty>
            ) : (
              tranches.map(t => (
                <Row key={t.trancheId} style={{ cursor: 'default' }}>
                  <RowMain>
                    <RowTitle>{t.trancheName} <span style={{ color: '#94a3b8', fontWeight: 400 }}>· {t.trancheClass}</span></RowTitle>
                    <RowSub><Mono>{t.cusip || '—'}</Mono>{t.couponType ? ` · ${t.couponType}` : ''}{t.couponRate != null ? ` ${t.couponRate}%` : ''}{t.originalCreditSupportPct != null ? ` · CS ${t.originalCreditSupportPct}%` : ''}</RowSub>
                  </RowMain>
                </Row>
              ))
            )}
          </Card>
        )}
      </Cols>
    </Wrap>
  );
};

export default StructuredProductsScreen;
